import type { GoApi } from "../shared/opencode-go-protocol.js";
import type { GoGatewayRoute } from "./components/oauth/GoGatewayRouteView.js";
/**
 * Fetch layer + react-query hooks for the integrated coding-subscription
 * OAuth routes. Unlike the usage-stats API these endpoints answer with bare
 * JSON documents and `{error, code?}` failures; both sides are validated
 * here with the shared zod contracts.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { type ZodType, z } from "zod";
import {
	type CapabilitySettingsPatch,
	type CapabilitySettingsSnapshot,
	CapabilitySettingsSnapshotSchema,
	CODING_OAUTH_PATHS,
	type CodingOAuthProviderSlug,
	type CodingOAuthWebStatus,
	CodingOAuthWebStatusSchema,
	GatewayKeyRevealSchema,
	type GatewayPublicStatus,
	GatewayPublicStatusSchema,
	ImagineCredentialStatusSchema,
	LoginChallengeSchema,
	OAuthImportCancelResultSchema,
	type OAuthImportCommitResult,
	OAuthImportCommitResultSchema,
	type OAuthImportPreview,
	OAuthImportPreviewSchema,
	OAuthImportSourcesResponseSchema,
	type OAuthSourceKind,
	OpenCodeGoConnectionStatusSchema,
	type OpenCodeGoModel,
	OpenCodeGoModelsResponseSchema,
} from "../shared/coding-oauth.js";
import { hubApiHeaders } from "./hub-headers.js";
import { usageQueryClient } from "./queries.js";

export class CodingOAuthApiError extends Error {
	readonly status: number;
	readonly code: string | undefined;

	constructor(message: string, status: number, code?: string) {
		super(message);
		this.name = "CodingOAuthApiError";
		this.status = status;
		this.code = code;
	}
}

function errorFrom(payload: unknown, status: number): CodingOAuthApiError {
	if (payload !== null && typeof payload === "object") {
		const record = payload as { error?: unknown; code?: unknown };
		const message = typeof record.error === "string" && record.error !== "" ? record.error : `HTTP ${status}`;
		const code = typeof record.code === "string" ? record.code : undefined;
		return new CodingOAuthApiError(message, status, code);
	}
	return new CodingOAuthApiError(`HTTP ${status}`, status);
}

export async function callCodingOAuth<T>(path: string, schema: ZodType<T>, init: RequestInit = {}): Promise<T> {
	const write = init.method !== undefined && init.method !== "GET";
	const headers = hubApiHeaders(path, write, init.headers);
	const response = await fetch(path, { ...init, headers });
	let payload: unknown = null;
	try {
		payload = await response.json();
	} catch {
		payload = null;
	}
	if (!response.ok) throw errorFrom(payload, response.status);
	return schema.parse(payload);
}

function postCodingOAuth<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
	return callCodingOAuth(path, schema, { method: "POST", body: JSON.stringify(body) });
}

const CODING_OAUTH_KEY = "coding-oauth";

export async function invalidateCodingOAuthQueries(): Promise<void> {
	await usageQueryClient.invalidateQueries({ queryKey: [CODING_OAUTH_KEY] });
}

export function useCodingOAuthStatusQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "status"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.status, CodingOAuthWebStatusSchema),
			enabled,
			refetchInterval: (query) => {
				const data = query.state.data;
				if (data === undefined) return 5_000;
				const signingIn = Object.values(data.providers).some((provider) => provider?.status === "signing-in");
				return signingIn ? 2_000 : 30_000;
			},
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useOpenCodeGoConnectionQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "opencode-go"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.opencodeGo, OpenCodeGoConnectionStatusSchema),
			staleTime: 0,
			refetchOnMount: "always",
			enabled,
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useOpenCodeGoModelsMutation() {
	return useMutation(
		{
			mutationFn: (credentialRef: string) =>
				callCodingOAuth(
					`${CODING_OAUTH_PATHS.opencodeGo}?models=1&credentialRef=${encodeURIComponent(credentialRef)}`,
					OpenCodeGoModelsResponseSchema,
				),
		},
		usageQueryClient,
	);
}

export function useOpenCodeGoCredentialMutation() {
	return useMutation(
		{
			mutationFn: ({ credentialRef, apiKey }: { credentialRef: string; apiKey?: string }) =>
				postCodingOAuth(
					CODING_OAUTH_PATHS.opencodeGo,
					{ action: "credential", credentialRef, ...(apiKey === undefined ? {} : { apiKey }) },
					OpenCodeGoConnectionStatusSchema,
				),
			onSuccess: async (response) => {
				usageQueryClient.setQueryData([CODING_OAUTH_KEY, "opencode-go"], response);
				await invalidateCodingOAuthQueries();
			},
		},
		usageQueryClient,
	);
}

export function useOpenCodeGoApplyMutation() {
	return useMutation(
		{
			mutationFn: ({
				credentialRef,
				models,
				expectedRevision,
				confirmConflicts,
				api,
			}: {
				credentialRef: string;
				models: readonly OpenCodeGoModel[];
				expectedRevision: number;
				confirmConflicts: boolean;
				api?: GoApi;
			}) =>
				postCodingOAuth(
					CODING_OAUTH_PATHS.opencodeGo,
					{ action: "apply", credentialRef, models, expectedRevision, confirmConflicts, ...(api ? { api } : {}) },
					OpenCodeGoConnectionStatusSchema,
				),
			onSuccess: async (response) => {
				usageQueryClient.setQueryData([CODING_OAUTH_KEY, "opencode-go"], response);
				await invalidateCodingOAuthQueries();
			},
		},
		usageQueryClient,
	);
}

export function useOpenCodeGoMigrateMutation() {
	return useMutation(
		{
			mutationFn: ({ expectedRevision, confirmConflicts }: { expectedRevision: number; confirmConflicts: boolean }) =>
				postCodingOAuth(
					CODING_OAUTH_PATHS.opencodeGo,
					{ action: "migrate", expectedRevision, confirmConflicts },
					OpenCodeGoConnectionStatusSchema,
				),
			onSuccess: async (response) => {
				usageQueryClient.setQueryData([CODING_OAUTH_KEY, "opencode-go"], response);
				await invalidateCodingOAuthQueries();
			},
		},
		usageQueryClient,
	);
}

export function useCodingOAuthLoginMutation() {
	return useMutation(
		{
			mutationFn: ({
				provider,
				method,
				accountMode,
				targetAccountId,
				confirmOverwrite,
			}: {
				provider: CodingOAuthProviderSlug;
				method?: string;
				accountMode?: "add" | "overwrite-active" | "reauthorize";
				targetAccountId?: string;
				confirmOverwrite?: boolean;
			}) =>
				postCodingOAuth(
					CODING_OAUTH_PATHS.login,
					{
						provider,
						...(method === undefined ? {} : { method }),
						...(accountMode === undefined ? {} : { accountMode }),
						...(targetAccountId === undefined ? {} : { targetAccountId }),
						...(confirmOverwrite === undefined ? {} : { confirmOverwrite }),
					},
					LoginChallengeSchema,
				),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

const CodeAcceptedSchema = z.object({ ok: z.literal(true) });

/** Opaque vendor usage document; shape is owned by the upstream provider. */
const CodexUsageSchema = z.record(z.string(), z.unknown());

export function useCodingOAuthCodeMutation() {
	return useMutation(
		{
			mutationFn: ({ provider, code }: { provider: CodingOAuthProviderSlug; code: string }) =>
				postCodingOAuth(CODING_OAUTH_PATHS.code, { provider, code }, CodeAcceptedSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodingOAuthCancelMutation() {
	return useMutation(
		{
			mutationFn: (provider: CodingOAuthProviderSlug) =>
				postCodingOAuth(CODING_OAUTH_PATHS.cancel, { provider }, CodingOAuthWebStatusSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodingOAuthLogoutMutation() {
	return useMutation(
		{
			mutationFn: (provider: CodingOAuthProviderSlug) =>
				postCodingOAuth(CODING_OAUTH_PATHS.logout, { provider }, CodingOAuthWebStatusSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodingOAuthModelsMutation() {
	return useMutation(
		{
			mutationFn: ({
				provider,
				selected,
				selectionMode,
			}: {
				provider: CodingOAuthProviderSlug;
				selected: readonly string[];
				selectionMode?: "default" | "selected";
			}) =>
				postCodingOAuth(CODING_OAUTH_PATHS.models, { provider, selected, selectionMode }, CodingOAuthWebStatusSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodingOAuthSetActiveAccountMutation() {
	return useMutation(
		{
			mutationFn: ({ provider, accountId }: { provider: CodingOAuthProviderSlug; accountId: string }) =>
				postCodingOAuth(CODING_OAUTH_PATHS.accountsSetActive, { provider, accountId }, CodingOAuthWebStatusSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodingOAuthRemoveAccountMutation() {
	return useMutation(
		{
			mutationFn: ({ provider, accountId }: { provider: CodingOAuthProviderSlug; accountId: string }) =>
				postCodingOAuth(CODING_OAUTH_PATHS.accountsRemove, { provider, accountId }, CodingOAuthWebStatusSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useOAuthSourcesQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "sources"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.sources, OAuthImportSourcesResponseSchema),
			enabled,
			staleTime: 15_000,
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useOAuthSourcePreviewMutation() {
	return useMutation(
		{
			mutationFn: (kind: OAuthSourceKind): Promise<OAuthImportPreview> =>
				postCodingOAuth(CODING_OAUTH_PATHS.sourcePreview, { kind }, OAuthImportPreviewSchema),
		},
		usageQueryClient,
	);
}

export function useOAuthSourceCommitMutation() {
	return useMutation(
		{
			mutationFn: (input: {
				kind: OAuthSourceKind;
				previewId: string;
				confirmOverwrite?: boolean;
			}): Promise<OAuthImportCommitResult> =>
				postCodingOAuth(CODING_OAUTH_PATHS.sourceCommit, input, OAuthImportCommitResultSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useOAuthSourceCancelMutation() {
	return useMutation(
		{
			mutationFn: (previewId: string) =>
				postCodingOAuth(CODING_OAUTH_PATHS.sourceCancel, { previewId }, OAuthImportCancelResultSchema),
		},
		usageQueryClient,
	);
}

export function useGatewayStatusQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "gateway"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.gateway, GatewayPublicStatusSchema),
			enabled,
			refetchInterval: 15_000,
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useGatewayPatchMutation() {
	return useMutation(
		{
			mutationFn: (patch: {
				enabled?: boolean;
				port?: number;
				opencodeGoEnabled?: boolean;
				opencodeGoRoute?: GoGatewayRoute | null;
			}): Promise<GatewayPublicStatus> =>
				callCodingOAuth(CODING_OAUTH_PATHS.gateway, GatewayPublicStatusSchema, {
					method: "PATCH",
					body: JSON.stringify(patch),
				}),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useGatewayRevealMutation() {
	return useMutation(
		{
			mutationFn: () => postCodingOAuth(CODING_OAUTH_PATHS.gatewayReveal, {}, GatewayKeyRevealSchema),
		},
		usageQueryClient,
	);
}

export function useGatewayRotateMutation() {
	return useMutation(
		{
			mutationFn: () => postCodingOAuth(CODING_OAUTH_PATHS.gatewayRotate, {}, GatewayKeyRevealSchema),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCapabilitiesQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "capabilities"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.capabilities, CapabilitySettingsSnapshotSchema),
			enabled,
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useCapabilitiesPatchMutation() {
	return useMutation(
		{
			mutationFn: ({
				patch,
				expectedRevision,
			}: {
				patch: CapabilitySettingsPatch;
				expectedRevision: number;
			}): Promise<CapabilitySettingsSnapshot> =>
				callCodingOAuth(CODING_OAUTH_PATHS.capabilities, CapabilitySettingsSnapshotSchema, {
					method: "PATCH",
					body: JSON.stringify({ patch, expectedRevision }),
				}),
			onSuccess: invalidateCodingOAuthQueries,
		},
		usageQueryClient,
	);
}

export function useCodexUsageQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "codex-usage"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.codexUsage, CodexUsageSchema),
			enabled,
			retry: 1,
		},
		usageQueryClient,
	);
}

export function useImagineCredentialQuery(enabled = true) {
	return useQuery(
		{
			queryKey: [CODING_OAUTH_KEY, "imagine-credential"],
			queryFn: () => callCodingOAuth(CODING_OAUTH_PATHS.imagineCredential, ImagineCredentialStatusSchema),
			enabled,
			retry: 1,
		},
		usageQueryClient,
	);
}

export type { CodingOAuthWebStatus };
